import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon } from 'lucide-react';
import { formatPrice, upsells } from '../data/upsells';
import { plans } from '../data/plans';
import { type UpsellVariantId } from '../utils/metaPixel';
import { goToCheckout, UPSELL_IDS } from '../utils/checkout';
type UpsellModalProps = {
  planId: 'starter' | 'complete' | null;
  onClose: () => void;
};
const FOCUSABLE = 'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
function lockBodyScroll() {
  const y = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${y}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.overflow = 'hidden';
  document.body.dataset.scrollY = String(y);
}
function unlockBodyScroll() {
  const y = Number(document.body.dataset.scrollY || 0);
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.overflow = '';
  delete document.body.dataset.scrollY;
  window.scrollTo(0, y);
}
export function UpsellModal({
  planId,
  onClose
}: UpsellModalProps) {
  const [selected, setSelected] = useState<UpsellVariantId[]>([]);
  const [shown, setShown] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const planIdRef = useRef<'starter' | 'complete' | null>(planId);
  planIdRef.current = planId;
  const open = planId !== null;
  const plan = plans.find(item => item.id === planId);
  const rows = upsells.filter(item => !item.starterOnly || planId === 'starter');
  useEffect(() => {
    if (!open) {
      setShown(false);
      setSelected([]);
      return;
    }
    triggerRef.current = document.activeElement as HTMLElement | null;
    lockBodyScroll();
    const raf = window.requestAnimationFrame(() => setShown(true));
    const focusTimer = window.setTimeout(() => {
      sheetRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    }, 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !sheetRef.current) return;
      const nodes = Array.from(sheetRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(node => !node.hasAttribute('disabled'));
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.cancelAnimationFrame(raf);
      window.clearTimeout(focusTimer);
      unlockBodyScroll();
      triggerRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const toggle = (id: UpsellVariantId) => {
    setSelected(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };
  const extrasTotal = rows.filter(row => selected.includes(row.id)).reduce((sum, row) => sum + row.price, 0);
  const total = Math.round(((plan?.price ?? 0) + extrasTotal) * 100) / 100;
  function isChecked(elementId: string): boolean {
    const input = document.getElementById(elementId) as HTMLInputElement | null;
    return input?.checked === true;
  }

  /** withExtras=false is the decline path: base plan only. */
  function proceed(withExtras: boolean) {
    const currentPlan = planIdRef.current;
    if (!currentPlan) return;
    const braveMind = withExtras && isChecked(UPSELL_IDS.braveMind);
    const littleEngineer = withExtras && isChecked(UPSELL_IDS.littleEngineer);
    const focusLab = withExtras && isChecked(UPSELL_IDS.focusLab);
    const basePrice = plans.find(item => item.id === currentPlan)?.price ?? 0;
    const priceOf = (id: UpsellVariantId) => upsells.find(row => row.id === id)?.price ?? 0;
    const value = Math.round((basePrice + (braveMind ? priceOf('braveMind') : 0) + (littleEngineer ? priceOf('littleEngineer') : 0) + (focusLab ? priceOf('focusLab') : 0)) * 100) / 100;
    goToCheckout(currentPlan, braveMind, littleEngineer, focusLab, value);
  }
  if (!open || !plan || typeof document === 'undefined') return null;
  return createPortal(<div className="upsell-modal" data-id="element-203" data-mp="125lmm1upvbzm">
      <div aria-hidden="true" onClick={onClose} className="fixed inset-0 transition-opacity duration-200 ease-out" style={{
      zIndex: 9998,
      backgroundColor: 'rgba(20,17,15,.55)',
      opacity: shown ? 1 : 0
    }} data-id="element-204" data-mp="x94yv8rfizn2" />

      <div ref={sheetRef} role="dialog" aria-modal="true" aria-label="Add to your kit before checkout" className={`fixed bottom-0 left-0 right-0 flex max-h-[85dvh] flex-col rounded-t-[24px] bg-bg transition-[transform,opacity] duration-200 ease-out motion-reduce:transform-none motion-reduce:transition-opacity md:bottom-auto md:left-1/2 md:right-auto md:top-1/2 md:w-full md:max-w-[540px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-[20px] ${shown ? 'translate-y-0' : 'translate-y-full'}`} style={{
      zIndex: 9999,
      opacity: shown ? 1 : 0
    }} data-id="element-205" data-mp="1x8f7dcranvbg">
        <div className="min-h-0 flex-1 overflow-y-auto px-[26px] pb-4 pt-[26px]" style={{
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-206" data-mp="2pwkuu79qfyez">
          <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-star" data-id="element-207" data-mp="2yy2dnxeiye7d">
            One-time offer — this page only
          </p>

          <h2 className="mt-2 text-left text-[22px] font-extrabold text-ink" data-id="element-208" data-mp="33xf0wj9b5nah">
            Add anything below and it ships with your kit.
          </h2>

          <div className="mt-5 space-y-2.5" data-id="element-209" data-mp="ztaotowmb3cp">
            {rows.map(row => {
            const checked = selected.includes(row.id);
            return <label key={row.id} className="flex cursor-pointer items-start gap-3 rounded-[12px] p-3.5 transition-colors duration-150 ease-out" style={{
              border: checked ? '1px solid #12A05A' : '1px dashed rgba(20,17,15,0.30)'
            }} data-id="element-210" data-mp="1dlqqomds8vc1">
                  <input id={UPSELL_IDS[row.id]} type="checkbox" checked={checked} onChange={() => toggle(row.id)} className="sr-only" data-id="element-211" data-mp="1wjtgv4i2gybf" />
                  <span aria-hidden="true" className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-[6px]" style={{
                border: checked ? '2px solid #12A05A' : '2px solid rgba(20,17,15,0.35)'
              }} data-id="element-212" data-mp="27d37imxyhwj">
                    {checked ? <CheckIcon className="h-4 w-4 text-[#12A05A]" strokeWidth={3} data-id="element-213" data-mp="200fvanyluvz2" /> : null}
                  </span>

                  {row.image ? <img src={row.image} alt="" aria-hidden="true" loading="lazy" decoding="async" className="h-14 w-14 flex-none self-start rounded-[8px] object-cover" style={{
                border: '1px solid rgba(20,17,15,.12)'
              }} data-id="element-214" data-mp="1q2i7u2okl37i" /> : <span aria-hidden="true" className="flex h-14 w-14 flex-none items-center justify-center self-start rounded-[8px] bg-[#dcdcdc] p-1 text-center text-[7px] font-medium uppercase leading-tight tracking-[0.08em] text-[#5a5a5a]" style={{
                border: '1px solid rgba(20,17,15,.12)'
              }} data-id="element-215" data-mp="3bhfnljkydat3">
                      {row.thumbLabel}
                    </span>}

                  <span className="min-w-0 flex-1 text-left" data-id="element-216" data-mp="1ojzrsqpld3z4">
                    <span className="block text-[15px] font-extrabold leading-[1.3]" data-id="element-217" data-mp="1ohnfou6ilntz">
                      {row.title}
                    </span>

                    <span className="mt-1 flex items-baseline gap-2" data-id="element-218" data-mp="1fis93gzk6bq5">
                      <span className="text-[16px] font-extrabold" data-id="element-219" data-mp="1ajyutmcg6gox">{formatPrice(row.price)}</span>
                      {row.compareAt ? <span className="text-[13px] text-ink/45 line-through" data-id="element-220" data-mp="15lxa6wzdi9bc">
                          {formatPrice(row.compareAt)}
                        </span> : null}
                    </span>

                    <span className="mt-1.5 block text-[13px] italic leading-[1.45] text-ink/70" data-id="element-221" data-mp="2zzetg7sqwk0k">
                      {row.description}
                    </span>
                  </span>
                </label>;
          })}
          </div>
        </div>

        <div className="relative flex-none bg-bg px-5 pt-4" style={{
        borderTop: '1px solid rgba(20,17,15,.12)',
        paddingBottom: 'calc(16px + env(safe-area-inset-bottom))'
      }} data-id="element-222" data-mp="3h7xvbi00c3e9">
          <div aria-hidden="true" className="pointer-events-none absolute -top-6 left-0 right-0 h-6" style={{
          background: 'linear-gradient(to bottom, rgba(246,239,230,0), #F6EFE6)'
        }} data-id="element-223" data-mp="2piuqo147kr13" />

          <p key={total} className="text-center text-[18px] font-extrabold" aria-live="polite" data-id="element-224" data-mp="33h2p6dgw80ed">
            Total: {formatPrice(total)}
          </p>

          <button type="button" onClick={() => proceed(true)} className="mt-3 h-[52px] w-full rounded-[999px] bg-[#12A05A] text-[16px] font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-[#0E8C4E] active:translate-y-[1px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink" data-id="element-225" data-mp="ipsg6bxsexoc">
            Continue to checkout →
          </button>

          <button type="button" onClick={() => proceed(false)} className="mx-auto mt-3 block text-[14px] text-ink/60 underline underline-offset-4" data-id="element-226" data-mp="1gxfmp5rcbfz4">
            No thanks, just my kit
          </button>
        </div>
      </div>
    </div>, document.body);
}